<script setup lang="ts">
import { Loader } from '@googlemaps/js-api-loader'

const props = defineProps<{
  title: StoryBlokHeading
  mapLat: string
  mapLng: string
  mapMarker: StoryBlokImage
  buttonText?: string
  buttonLink?: StoryBlokLink
  type: 'text' | 'icons'
  subtitle: string
  text: StoryBlokRichText
  address: StoryBlokRichText
  webLink?: StoryBlokLink
  webName?: string
  phone?: string
}>()

const config = useRuntimeConfig()
const mapFrame = ref<any>()

const loader = new Loader({
  apiKey: config.public.googleApiKey,
  version: 'weekly',
})
const options = {
  center: {
    lat: Number(props.mapLat),
    lng: Number(props.mapLng),
  },
  zoom: 14,
}
onMounted(() => {
  loader
    .load()
    .then((google) => {
      const map = new google.maps.Map(mapFrame.value, options)
      const _marker = new google.maps.Marker({
        position: options.center,
        icon: props.mapMarker.filename,
        map,
      })
    })
})
const phoneNumbers = props.phone?.split(',')
</script>

<template>
  <div class="flex flex-col gap-6 items-left md:items-center">
    <StoryblokHeading :title="title" class="max-w-780px" />
    <div
      :class="[type === 'text' ? 'flex-col-reverse lg:flex-row-reverse' : 'flex-col lg:flex-row']"
      class="shadow-passive bg-white rounded-xl w-full  flex lg:items-center"
    >
      <div ref="mapFrame" class="w-full rounded-lg h-340px lg:min-w-1/2 lg:max-w-700px" :class="type === 'icons' && 'lg:w-1/2'" />
      <div v-if="type === 'text'" class="flex flex-col flex-grow gap-5 p-4 md:p-8">
        <h3 v-if="subtitle">
          {{ subtitle }}
        </h3>
        <StoryblokRichText v-if="useHasContent(address)" :document="address" class="headline-4" />
        <StoryblokRichText v-if="useHasContent(text)" :document="text" />
      </div>
      <div v-else class="w-full flex flex-col gap-4 p-4 md:p-8">
        <div class="item">
          <div class="item-icon w-40px md:w-80px">
            <AppIcon icon="far-home-lg-alt" size="40" msize="20" class="text-primary" />
          </div>
          <StoryblokRichText :document="address" class="w-3/4" />
        </div>
        <div class="item">
          <div class="item-icon">
            <AppIcon icon="far-globe" size="40" msize="20" class="text-primary" />
          </div>
          <StoryblokLink v-bind="webLink">
            {{ webName }}
          </StoryblokLink>
        </div>
        <div class="item">
          <div class="item-icon">
            <AppIcon icon="far-phone-alt" size="40" msize="20" class="text-primary" />
          </div>
          <div>
            <a v-for="phoneNumber in phoneNumbers" :href="`tel:${phoneNumber}`">{{ phoneNumber }}</a>
          </div>
        </div>
      </div>
    </div>
    <StoryblokButton v-if="buttonText" class="w-full md:w-auto" :text="buttonText" size="large" :link="buttonLink" />
  </div>
</template>

<style lang="scss" scoped>
.item {
  @apply flex items-center gap-5;
  .item-icon {
    @apply flex items-center justify-center w-10 h-10 md:w-20 md:h-20 rounded-1/2 bg-primary bg-opacity-15;
  }
  & > * {
    @apply text-lg font-semibold flex flex-col;
  }
}
</style>
